import React, { useEffect, useState } from 'react';

import { IdcardOutlined } from '@ant-design/icons';
import { Card, Col, DatePicker, Form, Input, Row, Space, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminUserDetail } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { DocumentValidator } from 'utils/DocumentValidator';
import { FunctionsHelper } from 'utils/FunctionsHelper';

import appI18n from '../../../../config/i18n';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

interface Props {
  detail: AdminUserDetail;
  onUpdated: (next: AdminUserDetail) => void;
}

const getDocumentTypeKey = (countryCode: string): string => {
  const upper = (countryCode || '').toUpperCase();
  if (upper === 'CL' || upper === 'PE' || upper === 'CO') return upper;
  return 'default';
};

const getInvalidValidationKey = (countryCode: string): string => {
  switch ((countryCode || '').toUpperCase()) {
    case 'CL':
      return 'document_invalid_cl';
    case 'PE':
      return 'document_invalid_pe';
    case 'CO':
      return 'document_invalid_co';
    default:
      return 'document_invalid_default';
  }
};

const buildValues = (detail: AdminUserDetail, countryCode: string) => ({
  rut: detail.profile.rut
    ? (countryCode || '').toUpperCase() === 'CL'
      ? DocumentValidator.formatByCountry(detail.profile.rut, countryCode)
      : detail.profile.rut
    : '',
  documentExpirationDate: detail.profile.documentExpirationDate
    ? dayjs(detail.profile.documentExpirationDate)
    : null,
});

function IdentitySection({ detail, onUpdated }: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const watchedRut = Form.useWatch('rut', form);
  const hasDocumentNumberEdit = !!(
    watchedRut && (watchedRut as string).trim().length > 0
  );
  const hasDocumentNumberView = !!(
    detail.profile.rut && detail.profile.rut.trim().length > 0
  );
  const hasDocumentNumber = editing
    ? hasDocumentNumberEdit
    : hasDocumentNumberView;

  const countryCode = detail.user.countryCode;
  const documentTypeLabel = t(
    `scenes.leads.components.detail_lead_modal.sections.identity.document_types.${getDocumentTypeKey(
      countryCode
    )}`
  );

  useEffect(() => {
    form.setFieldsValue(buildValues(detail, countryCode));
  }, [detail, countryCode, form]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (saving) return;
    form.setFieldsValue(buildValues(detail, countryCode));
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const cleanRut = values.rut
        ? (countryCode || '').toUpperCase() === 'CL'
          ? DocumentValidator.cleanRut(values.rut)
          : (values.rut as string).trim()
        : null;

      const response = await LeadService.updateProfileIdentity(detail.user.id, {
        rut: cleanRut,
        documentExpirationDate: values.documentExpirationDate
          ? dayjs(values.documentExpirationDate).format('YYYY-MM-DD')
          : null,
      });

      if (response.success) {
        message.success(
          t(
            'scenes.leads.components.detail_lead_modal.sections.identity.messages.update_success'
          )
        );
        const refreshed = await LeadService.getById(detail.user.id);
        if (refreshed.success) {
          onUpdated(refreshed.data as AdminUserDetail);
        }
        setEditing(false);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.leads.components.detail_lead_modal.sections.identity.messages.update_failed'
            ),
          3.5
        );
      }
    } catch (e) {
      // validation error
    } finally {
      setSaving(false);
    }
  };

  const dateFormat = FunctionsHelper.getDateFormatByLang(i18n.language);
  const fieldsDisabled = !editing || saving;

  return (
    <Card
      className={
        editing
          ? 'lead-section-card lead-section-card--editing'
          : 'lead-section-card'
      }
      size="small"
      title={
        <Space size={8}>
          <IdcardOutlined />
          <span>
            {t(
              'scenes.leads.components.detail_lead_modal.sections.identity.title'
            )}
          </span>
        </Space>
      }
      extra={<SectionActions editing={editing} onEdit={handleEdit} />}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={!editing}
        onValuesChange={(changed) => {
          if (!editing) return;
          if ('rut' in changed) {
            const v = (changed.rut as string) || '';
            if (v.trim().length === 0) {
              form.setFieldValue('documentExpirationDate', null);
            }
          }
        }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.identity.labels.document_type'
              )}
            >
              <Input value={documentTypeLabel} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="rut"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.identity.labels.document_number'
              )}
              getValueFromEvent={(e) =>
                DocumentValidator.normalizeInputByCountry(
                  e.target.value as string,
                  countryCode
                )
              }
              rules={[
                {
                  validator: (_, rawValue) => {
                    const docValue = (rawValue as string) || '';
                    if (docValue.trim().length === 0) {
                      return Promise.resolve();
                    }
                    const valid = DocumentValidator.validateByCountry(
                      docValue,
                      countryCode
                    );
                    if (!valid) {
                      return Promise.reject(
                        new Error(
                          t(
                            `scenes.leads.components.detail_lead_modal.sections.identity.validations.${getInvalidValidationKey(
                              countryCode
                            )}`
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.identity.placeholders.document_number'
                )}
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="documentExpirationDate"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.identity.labels.expiration_date'
              )}
            >
              <DatePicker
                style={{ width: '100%' }}
                format={dateFormat}
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.identity.placeholders.expiration_date'
                )}
                disabled={fieldsDisabled || !hasDocumentNumber}
              />
            </Form.Item>
          </Col>
        </Row>

        {editing && (
          <SectionEditFooter
            saving={saving}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}
      </Form>
    </Card>
  );
}

export default IdentitySection;
